/**
 * Scorciatoia globale: Cmd/Ctrl+K mette in maiuscolo il testo selezionato
 * nel campo di testo attivo (input o textarea), in tutta l'app.
 * @module shared/hooks/useUppercaseSelectionShortcut
 */

import { useEffect } from "react";

const TEXTUAL_INPUT_TYPES = new Set(["text", "search", "tel", "url"]);
const SENSITIVE_AUTOCOMPLETE = /password|one-time-code|cc-number|cc-csc/i;

function isEligibleField(el) {
	if (!(el instanceof HTMLElement)) return false;
	if (el.disabled || el.readOnly) return false;
	if (el instanceof HTMLTextAreaElement) return true;
	if (el instanceof HTMLInputElement) {
		if (SENSITIVE_AUTOCOMPLETE.test(el.getAttribute("autocomplete") || "")) return false;
		return TEXTUAL_INPUT_TYPES.has(el.type);
	}
	return false;
}

function setNativeValue(el, value) {
	const proto = el instanceof HTMLTextAreaElement ? HTMLTextAreaElement.prototype : HTMLInputElement.prototype;
	const setter = Object.getOwnPropertyDescriptor(proto, "value").set;
	setter.call(el, value);
}

function handleKeyDown(event) {
	if (!(event.metaKey || event.ctrlKey) || event.altKey || event.shiftKey) return;
	if (event.key?.toLowerCase() !== "k") return;

	const el = document.activeElement;
	if (!isEligibleField(el)) return;

	const { selectionStart, selectionEnd, value } = el;
	if (selectionStart === null || selectionStart === selectionEnd) return;

	event.preventDefault();

	const upper = value.slice(selectionStart, selectionEnd).toUpperCase();
	const newValue = value.slice(0, selectionStart) + upper + value.slice(selectionEnd);

	setNativeValue(el, newValue);
	el.dispatchEvent(new Event("input", { bubbles: true }));
	el.setSelectionRange(selectionStart, selectionEnd);
}

/**
 * Registra il listener globale per tutta la vita del componente che lo monta.
 * Chiamare una sola volta, in un componente sempre montato (la root App).
 */
export default function useUppercaseSelectionShortcut() {
	useEffect(() => {
		document.addEventListener("keydown", handleKeyDown);
		return () => document.removeEventListener("keydown", handleKeyDown);
	}, []);
}
